import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';

const ActivityList = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canActivityList = (key) => can(key, 'activityList');

    // Pagination
    const pagination = usePagination(10);

    // Search
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    const [isLoading, setIsLoading] = useState(false);

    // Selection
    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Date filter
    const [fromDate] = useState('');
    const [toDate] = useState('');

    // Data and form state
    const [activityLists, setActivityLists] = useState([]);
    const [formData, setFormData] = useState({ name: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    // Fetch activity lists
    const fetchActivityLists = useCallback(async (searchValue = appliedSearchTerm) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/md/activity-list-page`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue,
                    from: fromDate,
                    to: toDate
                }
            });
            setActivityLists(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setTotal(response.data.totalElements);
            pagination.setCurrentPageItems(response.data.content.length);
        } catch (error) {
            let errorMessage = 'Failed to fetch activity lists.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view activity lists.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while fetching activity lists.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching activity lists.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
            setActivityLists([]);
        } finally {
            setIsLoading(false);
        }
    }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

    useEffect(() => {
        fetchActivityLists(appliedSearchTerm);
    }, [pagination.page, pagination.size, appliedSearchTerm]);

    // Open modal for create
    const handleCreate = () => {
    if (!canActivityList('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '' });
        setEditingId(null);
        setErrors({});
        const modal = new window.bootstrap.Modal(document.getElementById('modal-activity-list'));
        modal.show();
    };

    // Open modal for edit
    const handleEdit = (item) => {
    if (!canActivityList('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({ name: item.name });
        setEditingId(item.id);
        setErrors({});
        const modal = new window.bootstrap.Modal(document.getElementById('modal-activity-list'));
        modal.show();
    };

    // Real-time validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'name') {
            error = validateField('name', value, { required: true });
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // Unified handleSubmit for both create and edit
    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateField('name', formData.name, { required: true });
        if (error) {
            setErrors({ name: error });
            return;
        }
        try {
            if (editingId) {
                await api.put(`/md/activity-list/${editingId}`, { id: editingId, name: formData.name });
                setSuccessMessage('Activity list has been successfully updated.');
            } else {
                await api.post('/md/activity-list', { name: formData.name });
                setSuccessMessage('Activity list has been successfully added.');
            }
            setEditingId(null);
            setFormData({ name: '' });
            setErrors({});
            await fetchActivityLists();
            const modal = document.getElementById('modal-activity-list');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
            if (bootstrapModal) {
                bootstrapModal.hide();
                setTimeout(() => setShowSuccessModal(true), 300);
            } else {
                setShowSuccessModal(true);
            }
        } catch (error) {
            let errorMessage = 'Failed to save activity list.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = 'Invalid activity list data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 409:
                        errorMessage = 'An activity list with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${formData.name}"`;
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while saving.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving the activity list.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Delete selected (bulk)
    const deleteSelected = () => {
    if (!canActivityList('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one activity list to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await api.delete('/md/activity-list/bulk', { data: selectedIds });
                setSelectedIds([]);
                fetchActivityLists();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected activity lists deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                let errorMessage = 'Failed to delete activity lists.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing';
                            break;
                        case 404:
                            errorMessage = 'One or more activity lists not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data.message || 'Internal server error during deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the items.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    // Modal cleanup
    useEffect(() => {
        const modal = document.getElementById('modal-activity-list');
        modal?.addEventListener('hidden.bs.modal', () => {
            setFormData({ name: '' });
            setEditingId(null);
        });
        return () => {
            modal?.removeEventListener('hidden.bs.modal', () => { });
        };
    }, []);

    const columns = [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'cuser', label: 'Created By', sortable: true, render: (value) => value ? value.username : 'Unknown' },
        { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleString() : '-' },
    ];

    return (
        <>
                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 className="page-title d-flex align-items-center">
                                    Activity List
                                </h2>
                            </div>
                            <div className="col-auto ms-auto d-print-none">
                                <div className="btn-list">
                                    <button type="button" className="btn btn-primary btn-5 d-none d-sm-inline-block"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                        Create
                                    </button>
                                    <button type="button" className="btn btn-primary btn-6 d-sm-none btn-icon"
                                        onClick={handleCreate} aria-label="Create new report">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page-body">
                    <div className="container-xl">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <GenericView
                                        data={activityLists}
                                        columns={columns}
                                        isLoading={isLoading || capabilitiesLoading}
                                        emptyStateMessage="No activity lists found"
                                        checkboxSelection={true}
                                        selectedRows={selectedIds}
                                        onSelectRow={(itemId, isSelected) => {
                                            if (isSelected) {
                                                setSelectedIds([...selectedIds, itemId]);
                                            } else {
                                                setSelectedIds(selectedIds.filter(id => id !== itemId));
                                            }
                                        }}
                                        onSelectAllRows={(itemIds) => {
                                            if (Array.isArray(itemIds) && itemIds.length > 0) {
                                                setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                            } else {
                                                setSelectedIds([]);
                                            }
                                        }}
                                        onEdit={canActivityList('edit') ? (item) => {
                                            setSelectedIds([item.id]);
                                            handleEdit(item);
                                        } : undefined}
                                        onDelete={canActivityList('delete') ? (item) => {
                                            setSelectedIds([item.id]);
                                            deleteSelected();
                                        } : undefined}
                                        searchComponent={
                                            <div className="d-flex gap-2">
                                                <SearchInput
                                                    value={searchTerm}
                                                    onChange={handleSearchInputChange}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSearchSubmit();
                                                        }
                                                    }}
                                                    placeholder="Search..."
                                                    uniqueId="activity-list-search"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleSearchSubmit}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                        }
                                        actionButtons={
                                            <div className="d-flex gap-2">
                                                {(canActivityList('edit') || canActivityList('delete')) && (
                                                    <div ref={actionsContainerRef} className="dropdown">
                                                        <a
                                                            ref={actionsToggleRef}
                                                            href="#"
                                                            className="btn dropdown-toggle"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded={isActionsOpen}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                const inst = actionsDropdownInstanceRef.current;
                                                                if (inst?.toggle) inst.toggle();
                                                                else setIsActionsOpen(prev => !prev);
                                                            }}
                                                        >Actions</a>
                                                        <div className="dropdown-menu">
                                                            {canActivityList('edit') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => {
                                                                    e.preventDefault();
                                                                    if (selectedIds.length === 1) {
                                                                        const selectedItem = activityLists.find(s => s.id === selectedIds[0]);
                                                                        if (selectedItem) handleEdit(selectedItem);
                                                                    }
                                                                }}
                                                                    style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                                    Edit
                                                                </a>
                                                            )}
                                                            {canActivityList('delete') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                                    style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                                    Delete
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        pagination={{
                                            currentPage: pagination.page + 1,
                                            totalRows: pagination.total,
                                            rowsPerPage: pagination.size,
                                            onPageChange: (page) => pagination.setPage(page - 1),
                                            onRowsPerPageChange: (size) => {
                                                pagination.setSize(size);
                                                pagination.setPage(0);
                                            }
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        uniqueId="activity-list-table"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            
            {/* Modal for add/edit activity list */}
            <div className="modal modal-blur fade" id="modal-activity-list" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingId ? 'Edit Activity List' : 'Add New Activity List'}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        name="name"
                                        placeholder="Enter Activity List"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                                    {editingId ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => { })}
                title="Delete Confirmation"
                message={selectedIds.length > 0
                    ? "Are you sure you want to delete the selected activity lists? This action cannot be undone."
                    : "Are you sure you want to delete this activity list? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />
            {/* Fail Modal */}
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Operation Failed"
                message={failMessage}
                buttonText="Close"
            />
        </>
    );
};

export default ActivityList;
